import { useEffect, useState } from "react";
import { toast } from "sonner";
import { createPatient } from "../api/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const EMPTY = { name: "", phone: "", email: "", gender: "", address: { line: "", city: "", state: "", pincode: "" } };
// India-only, 6 digits, no leading zero — mirrors PINCODE_PATTERN in
// medipro-backend/validations/validations.js. Required so every new patient
// lands on the Stats page's location map instead of the "no usable pincode"
// bucket.
const PINCODE_PATTERN = /^[1-9]\d{5}$/;

export default function AddPatientModal({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [pincodeLookupLoading, setPincodeLookupLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  useEffect(() => {
    const pincode = form.address.pincode;
    if (!/^\d{6}$/.test(pincode)) return;

    let cancelled = false;
    setPincodeLookupLoading(true);
    fetch(`https://api.postalpincode.in/pincode/${pincode}`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const postOffice = data?.[0]?.PostOffice?.[0];
        if (postOffice) {
          setForm((f) => ({ ...f, address: { ...f.address, city: postOffice.District, state: postOffice.State } }));
        } else {
          toast.error("Couldn't find that pincode — enter city/state manually.");
        }
      })
      .catch(() => toast.error("Couldn't look up pincode — enter city/state manually."))
      .finally(() => !cancelled && setPincodeLookupLoading(false));

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.address.pincode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and mobile number are required.");
      return;
    }
    if (!PINCODE_PATTERN.test(form.address.pincode)) {
      toast.error("Enter a valid 6-digit pincode.");
      return;
    }
    setSaving(true);
    try {
      await createPatient({ ...form, email: form.email || undefined, gender: form.gender || undefined });
      toast.success("Patient added.");
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.message || "Could not add patient.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add patient</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5">
            <Label>Name *</Label>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Mobile *</Label>
            <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Gender</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.gender}
              onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
            >
              <option value="">Select</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </select>
          </div>
          <div />

          <div className="col-span-2 space-y-1.5">
            <Label>Address</Label>
            <Input
              placeholder="House no., street, area"
              value={form.address.line}
              onChange={(e) => setForm((f) => ({ ...f, address: { ...f.address, line: e.target.value } }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Pincode *</Label>
            <Input
              maxLength={6}
              placeholder="e.g. 781001"
              value={form.address.pincode}
              onChange={(e) =>
                setForm((f) => ({ ...f, address: { ...f.address, pincode: e.target.value.replace(/\D/g, "") } }))
              }
            />
            {pincodeLookupLoading && <p className="text-xs text-muted-foreground">Looking up city/state…</p>}
            {form.address.pincode.length > 0 && !PINCODE_PATTERN.test(form.address.pincode) && (
              <p className="text-xs text-destructive">Enter a valid 6-digit pincode</p>
            )}
          </div>
          <div />
          <div className="space-y-1.5">
            <Label>City</Label>
            <Input
              value={form.address.city}
              onChange={(e) => setForm((f) => ({ ...f, address: { ...f.address, city: e.target.value } }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label>State</Label>
            <Input
              value={form.address.state}
              onChange={(e) => setForm((f) => ({ ...f, address: { ...f.address, state: e.target.value } }))}
            />
          </div>

          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !form.name || !form.phone || !PINCODE_PATTERN.test(form.address.pincode)}>
              {saving ? "Saving…" : "Add patient"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
